import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { auth, db } from "./firebase";

import Login from "./pages/Login";

import EditOrder from "./pages/EditOrder";
import ApproveOrder from "./pages/ApproveOrder";
import OperatorDashboard from "./pages/DashboardUltra";
import PendingOrders from "./pages/PendingOrders";
import DistributorEdit from "./pages/DistributorEdit";
import DistributorManager from "./pages/DistributorManager";
import DsManager from "./pages/DsManager";
import OutletMaster from "./pages/OutletMaster";
import DsMobileDashboard from "./pages/DsMobileDashboard";
import Help from "./pages/Help";
import SkuMaster from "./pages/SkuMaster";
import SkuManager from "./pages/SkuManager";
import DsOrderReport from "./pages/DsOrderReport";
import DsTravelReport from "./pages/DsTravelReport";

import MasterPendingOrders from "./pages/MasterPendingOrders";
import BulkOrderUpload from "./pages/BulkOrderUpload";



import BillingReports from "./pages/BillingReports";
import OrderHeadReport from "./pages/OrderHeadReport";
import OrderLinesReport from "./pages/OrderLinesReport";
import OrderLinesEdit from "./pages/OrderLinesEdit";


import Dashboard from "./pages/Dashboard";

import RequireAuth from "./components/RequireAuth";

import SalesSummary from "./pages/reports/SalesSummary";
import TallySheetReport from "./pages/reports/TallySheetReport";
import NewReport from "./pages/reports/NewReport";
import DmsUploadReport from "./pages/reports/DmsUploadReport";
import HsnSummaryReport from "./pages/reports/HsnSummaryReport";

import DistributorLogin from "./pages/DistributorLogin";
import DistributorOrder from "./pages/DistributorOrder";
import BasePage from "./pages/BasePage";


import WholesaleOrder from "./pages/WholesaleOrder";
import Swd from "./pages/swd";

import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";


import InwardAdd from "./pages/InwardAdd";
import InwardUpload from "./pages/InwardUpload";
import InwardList from "./pages/InwardList";
import WarehouseClose from "./pages/WarehouseClose";
import SupervisorOverride from "./pages/SupervisorOverride";
import DistributorDashboard from "./pages/DistributorDashboard";
import DistributorToDsBilling from "./pages/DistributorToDsBilling";
import DsToOutletBilling from "./pages/DsToOutletBilling";
import DistributorInventory from "./pages/DistributorInventory";
import CnFToDistributorBilling from "./pages/CnFToDistributorBilling";
import DistributorInvoiceList from "./pages/DistributorInvoiceList";
import DistributorInventoryReport from "./pages/DistributorInventoryReport";
import DistributorMobileDashboard from "./pages/DistributorMobileDashboard";



import Cashier from "./pages/Cashier";
import CashierApproval from "./pages/CashierApproval";

import CnfPurchase from "./pages/CnfPurchase";
import CnfStockLedger from "./pages/CnfStockLedger";
import SupplierManager from "./pages/SupplierManager";
import CnfManager from "./pages/CnfManager";
import CnfDashboardUltra from "./pages/CnfDashboardUltra";
import CnfStockLedgerBags from "./pages/CnfStockLedgerBags";
import RsmDashboard from "./pages/RsmDashboard";
import RsmSalesReports from "./pages/RsmSalesReports";
import RsmStockReports from "./pages/RsmStockReports";
import PrivacyPolicy from "./pages/PrivacyPolicy";

const SplashScreen = () => (
  <div style={{ 
    width: '100vw', height: '100vh', background: '#050505', 
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
    fontFamily: "'Inter', sans-serif", color: '#fff'
  }}>
    <div style={{ 
      width: 80, height: 80, marginBottom: 24, 
      border: '2px solid rgba(255,107,0,0.2)', borderRadius: 20,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      boxShadow: '0 0 40px rgba(255,107,0,0.1)'
    }}>
      <img src="/logo.svg" alt="QB" style={{ width: 40, height: 40, filter: 'drop-shadow(0 0 10px rgba(255,107,0,0.5))' }} />
    </div>
    <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, letterSpacing: '0.1em' }}>QUICKBILL <span style={{ color: '#ff6b00' }}>CORE</span></h2>
    <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 8, fontWeight: 700 }}>SYNCHRONIZING SECURE UPLINK...</p>
  </div>
);




// Error Boundary Component to prevent White Screen of Death
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) { return { hasError: true }; }
  componentDidCatch(error, errorInfo) { console.error("CRITICAL APP CRASH:", error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "#f8fafc", padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>🛠️</div>
          <h2 style={{ fontWeight: 900, color: "#1e293b" }}>System Recovery Required</h2>
          <p style={{ color: "#64748b", maxWidth: 400, marginBottom: 30 }}>The application encountered an unexpected internal error during sync.</p>
          <button 
            onClick={() => { localStorage.clear(); window.location.href = "/"; }}
            style={{ padding: "14px 28px", background: "#FF6B00", color: "white", border: "none", borderRadius: 12, fontWeight: 800, cursor: "pointer", boxShadow: "0 10px 20px rgba(255, 107, 0, 0.2)" }}
          >
            REPAIR & RESTART SESSION
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [role, setRole] = useState(localStorage.getItem("role") || null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          localStorage.setItem("email", user.email || "");
          
          // NON-BLOCKING: Set authChecked immediately so app can start rendering
          // using cached 'role' from localStorage if available.
          setAuthChecked(true);

          // Background Profile Sync
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const userData = snap.data();
            setRole(userData.role);
            localStorage.setItem("role", userData.role);

            // Resolve Entity Name
            let entityName = localStorage.getItem("entity_name") || "QuickBill System";
            try {
                if (userData.role === "distributor" && userData.partyId) {
                  const distSnap = await getDoc(doc(db, "partyMaster", userData.partyId));
                  if (distSnap.exists()) {
                    entityName = distSnap.data().name || distSnap.data().partyName || "Distributor";
                  }
                } else if (["admin", "superadmin", "operator", "ds_manager"].includes(userData.role)) {
                  const cnfSnap = await getDocs(collection(db, "cnf_master"));
                  if (!cnfSnap.empty) {
                    const firstCnf = cnfSnap.docs[0].data();
                    entityName = firstCnf.name || firstCnf.partyName || "C&F Entity";
                  }
                }
                localStorage.setItem("entity_name", entityName);
            } catch (err) {
                console.warn("Entity resolution failed (offline?):", err);
            }
          } else {
            // User authenticated but no profile doc?
            setRole(null);
          }
        } else {
          setRole(null);
          localStorage.removeItem("role");
          localStorage.removeItem("email");
          localStorage.removeItem("entity_name");
          setAuthChecked(true);
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        setAuthChecked(true); // Ensure we don't stay on white screen even on error
      }
    });

    return () => unsub();
  }, []);

  // Helper to get dashboard path based on role
  const getDashboardPath = (r) => {
    if (r === "ds") return "/ds-mobile";
    if (r === "distributor") return "/distributor-mobile";
    if (["superadmin", "admin", "operator"].includes(r)) return "/cnf-dashboard";
    if (r === "rsm") return "/rsm-dashboard";
    if (r === "ds_manager") return "/ds-manager";
    return "/dashboard";
  };

  if (!authChecked) return <SplashScreen />;


  return (
    <ErrorBoundary>
      <BrowserRouter>
      <Routes>
        {/* ROOT GATEKEEPER: Zero-Flash Redirect */}
        <Route path="/" element={role ? <Navigate to={getDashboardPath(role)} replace /> : <Login />} />

        {/* C&F Level Routes */}
        <Route path="/operator" element={
            <RequireAuth>
                {!role ? <div style={{ padding: 20, color: '#fff', background: '#050505', height: '100vh' }}>Synchronizing Access...</div> : 
                 ["admin", "superadmin", "operator"].includes(role) ? <OperatorDashboard /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}
            </RequireAuth>
        } />
        <Route path="/pending-orders" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <PendingOrders /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/edit-order/:orderId" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <EditOrder /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/approve-order/:orderId" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <ApproveOrder /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />

        <Route path="/sku-master" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "rsm"].includes(role) ? <SkuMaster /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />

        <Route path="/sku-manager" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <SkuManager /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/OrderLinesEdit" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <OrderLinesEdit /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-manager" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator", "rsm"].includes(role) ? <DistributorManager /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/ds-manager" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator", "distributor", "ds_manager", "rsm"].includes(role) ? <DsManager /> : <Navigate to="/" />}</RequireAuth>} />
        <Route path="/outlet-master" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "distributor", "operator", "rsm"].includes(role) ? <OutletMaster /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/ds-order-report" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "distributor", "operator"].includes(role) ? <DsOrderReport /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/ds-travel-report" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "distributor", "operator"].includes(role) ? <DsTravelReport /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/help" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "distributor", "operator"].includes(role) ? <Help /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/ds-mobile" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : role === "ds" ? <DsMobileDashboard /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />

        <Route path="/reports" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <BillingReports /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/reports/order-head" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <OrderHeadReport /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/reports/order-lines" element={<RequireAuth>{!role ? <div style={{ padding: 20 }}>Syncing...</div> : ["admin", "superadmin", "operator"].includes(role) ? <OrderLinesReport /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />


        <Route path="/reports/sales-summary" element={<RequireAuth><SalesSummary /></RequireAuth>} />
        <Route path="/reports/tally-sheet" element={<RequireAuth><TallySheetReport /></RequireAuth>} />
        <Route path="/reports/new-report" element={<RequireAuth><NewReport /></RequireAuth>} />
        <Route path="/reports/dms-upload" element={<RequireAuth><DmsUploadReport /></RequireAuth>} />
        <Route path="/reports/hsn-summary" element={<RequireAuth><HsnSummaryReport /></RequireAuth>} />

        {/* Inward + Closing for Admin, Superadmin, Operator */}
        <Route path="/inward-add" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <InwardAdd /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/inward-upload" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <InwardUpload /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-to-distributor-billing" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <CnFToDistributorBilling /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-purchase" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <CnfPurchase /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-stock-ledger" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <CnfStockLedger /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-stock-ledger-bags" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <CnfStockLedgerBags /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-dashboard" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <CnfDashboardUltra /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/supplier-manager" element={<RequireAuth>{!role ? null : ["admin", "superadmin"].includes(role) ? <SupplierManager /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/cnf-manager" element={<RequireAuth>{!role ? null : ["admin", "superadmin"].includes(role) ? <CnfManager /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-invoices" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <DistributorInvoiceList /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/inward-list" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <InwardList /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/warehouse-close" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator", "warehouse"].includes(role) ? <WarehouseClose /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />

        <Route path="/supervisor-override" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <SupervisorOverride /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />


        <Route path="/master-pending" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "operator"].includes(role) ? <MasterPendingOrders /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/bulk-order-upload" element={<BulkOrderUpload />} />




        <Route path="/distributor-login" element={<DistributorLogin />} />
        <Route path="/distributor-order" element={<DistributorOrder />} />
        <Route path="/base" element={<RequireAuth><BasePage /></RequireAuth>} />

        <Route path="/wholesale-order" element={<RequireAuth><WholesaleOrder /></RequireAuth>} />
        <Route path="/swd" element={<RequireAuth><Swd /></RequireAuth>} />

        <Route path="/distributor-dashboard" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor"].includes(role) ? <DistributorDashboard /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-bill-ds" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor"].includes(role) ? <DistributorToDsBilling /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/ds-bill-outlet" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor", "ds"].includes(role) ? <DsToOutletBilling /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-inventory" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor"].includes(role) ? <DistributorInventory /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-inventory-report" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor"].includes(role) ? <DistributorInventoryReport /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/distributor-mobile" element={<RequireAuth>{!role ? null : ["admin", "superadmin", "distributor"].includes(role) ? <DistributorMobileDashboard /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />


        <Route path="/cashier" element={<Cashier />} />
        <Route path="/cashier-Approval" element={<CashierApproval />} />

        {/* RSM Routes */}
        <Route path="/rsm-dashboard" element={<RequireAuth>{!role ? null : role === "rsm" ? <RsmDashboard /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/rsm/sales-reports" element={<RequireAuth>{!role ? null : role === "rsm" ? <RsmSalesReports /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />
        <Route path="/rsm/stock-reports" element={<RequireAuth>{!role ? null : role === "rsm" ? <RsmStockReports /> : <Navigate to={role === "ds_manager" ? "/ds-manager" : "/"} />}</RequireAuth>} />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />



      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
